import { AnyKindOfDictionary } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { store } from 'shared/store';
import { makeLog } from 'shared/utils/log';

const log = makeLog('api-loader');

export const useApiLoader = <T extends () => Promise<{ data: any }>>(
  apiFn: T,
  noInitialLoad = false,
  isUseCache = false,
) => {
  const [data, setData] = useState<Awaited<ReturnType<T>>['data']>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(-1);

  const loadData = useCallback(async () => {
    if (isUseCache) {
      const cachedData = _getCached(apiFn.name);
      if (cachedData) {
        log.debug(`Using cached data for (${apiFn.name})`);
        setData(cachedData);
        setHasLoaded(true);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError('');
      const { data } = await apiFn();
      setData(data);
      if (isUseCache) {
        log.debug(`Caching data for (${apiFn.name})`);
        _setCached(apiFn.name, data);
      }
      setHasLoaded(true);
      setStatus(-1);
    } catch (err) {
      setStatus((err as { response: { status: number } }).response?.status || -1);
      setError(`${err}`);
    } finally {
      setIsLoading(false);
    }
  }, [apiFn, isUseCache]);

  // Initial load
  useEffect(() => {
    if (!noInitialLoad) {
      void loadData();
    }
  }, [loadData, noInitialLoad]);

  return {
    data,
    isLoading,
    hasLoaded,
    error,
    status,
    loadData,
  };
};

const _makeHash = (fnName: string) =>
  `sb:api-loader:cache:${store.getState().auth.activeOrgId}:${fnName}`;
const _setCached = (fnName: string, data: AnyKindOfDictionary) => {
  sessionStorage.setItem(_makeHash(fnName), JSON.stringify(data));
};
const _getCached = (fnName: string) => {
  const cached = sessionStorage.getItem(_makeHash(fnName));
  if (cached) {
    return JSON.parse(cached);
  }
};

// Good lord: https://stackoverflow.com/questions/50321419/typescript-returntype-of-generic-function
class UseApiLoaderTypeWrapper<T extends () => Promise<{ data: any }>> {
  wrapped(f: T) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useApiLoader<T>(f);
  }
}

export type UseApiLoaderReturnType<T extends () => Promise<{ data: any }>> = ReturnType<
  UseApiLoaderTypeWrapper<T>['wrapped']
>;
